import { api, ApiError, NETWORK_ERROR_MESSAGE } from './api';

export interface UploadedMedia {
  id: string;
  url: string;
}

const MAX_SIDE = 2400;
const LIGHT_BYTES = 1.5 * 1024 * 1024;
const SERVER_IMAGE_BYTES = 5 * 1024 * 1024;
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm']);
// Formati che possono avere parti trasparenti (loghi): non diventano mai JPG.
const ALPHA_TYPES = new Set(['image/png', 'image/webp', 'image/avif', 'image/gif']);

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function renamed(name: string, type: string): string {
  const ext = type === 'image/webp' ? '.webp' : type === 'image/png' ? '.png' : '.jpg';
  return name.replace(/\.[^.]+$/, '') + ext;
}

/** Rimpicciolisce la foto; se qualcosa va storto restituisce null e si carica l'originale. */
async function shrink(original: File, bitmap: ImageBitmap, heic: boolean): Promise<File | null> {
  const ratio = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
  canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
  const context = canvas.getContext('2d');
  if (!context) return null;
  const alpha = ALPHA_TYPES.has(original.type);
  if (alpha) {
    // Safari non sa creare WebP e restituisce PNG: si usa il tipo davvero prodotto.
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, 'image/webp', 0.86);
    if (blob && blob.size <= SERVER_IMAGE_BYTES && (blob.type === 'image/webp' || blob.type === 'image/png'))
      return new File([blob], renamed(original.name, blob.type), { type: blob.type });
    return null;
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await toBlob(canvas, 'image/jpeg', 0.86);
  if (!blob || blob.type !== 'image/jpeg') return null;
  if (blob.size >= original.size && !heic) return null;
  return new File([blob], renamed(original.name, 'image/jpeg'), { type: 'image/jpeg' });
}

/**
 * Prepara la foto prima dell'invio: la legge subito in memoria (così un file su iCloud, WhatsApp o una chiavetta
 * non «cambia» durante l'invio) e rimpicciolisce le foto pesanti, che su una rete lenta facevano cadere la connessione.
 */
export async function prepareMediaFile(file: File): Promise<File> {
  if (file.size > 80 * 1024 * 1024)
    throw new ApiError('Questo file è troppo grande. Le foto devono pesare meno di 5 MB e i video meno di 20 MB.', 422);
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    throw new ApiError('Non riesco a leggere questo file dal computer. Se è su iCloud o in una cartella condivisa, scaricalo prima sul computer e riprova.', 0);
  }
  const inMemory = new File([bytes], file.name, { type: file.type });
  if (VIDEO_TYPES.has(file.type)) return inMemory;
  const heic = /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
  if (!file.type.startsWith('image/') && !heic) return inMemory;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return inMemory;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(inMemory);
  } catch {
    // Il browser non sa aprirla (es. HEIC su Chrome): la manda com'è e il server spiega cosa fare.
    return inMemory;
  }
  try {
    const big = Math.max(bitmap.width, bitmap.height) > MAX_SIDE || inMemory.size > LIGHT_BYTES;
    if (!big && !heic) return inMemory;
    return (await shrink(inMemory, bitmap, heic).catch(() => null)) || inMemory;
  } finally {
    bitmap.close();
  }
}

/** Unico punto da cui il gestionale carica foto e video: prepara il file e riprova da solo se la rete cade. */
export async function uploadMedia<T extends UploadedMedia = UploadedMedia>(siteId: string, file: File, altText = ''): Promise<T> {
  const ready = await prepareMediaFile(file);
  const video = VIDEO_TYPES.has(ready.type);
  const form = new FormData();
  form.append('file', ready);
  if (altText) form.append('altText', altText);
  const result = await api<{ media?: T }>(`/api/sites/${siteId}/media`, {
    method: 'POST',
    body: form,
    // I video (fino a 20 MB) su rete lenta possono metterci minuti: nessun limite di tempo e un solo nuovo tentativo.
    retries: video ? 1 : 3,
    timeoutMs: video ? undefined : 120_000,
  });
  if (!result.media?.url) throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  return result.media;
}
